/**
 * AIMDv2 – Step 3: Document Upload
 *
 * Three upload "cards" (Add Files, Add Text, Add URL) open modals for
 * different document source types. Below the cards:
 *  - A collapsible list of newly uploaded documents.
 *  - A collapsible list of previously available documents.
 *
 * Documents are fetched from the real backend via GET /api/orgDocs.
 * Uploads go through the real async processing endpoints.
 * Each document can be toggled on/off and new uploads can be removed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Collapse, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../../../../components/ui/DashboardSkeletons';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddFileModal from '../components/AddFileModal';
import AddTextModal from '../components/AddTextModal';
import AddUrlModal from '../components/AddUrlModal';
import DocumentRow from '../components/DocumentRow';
import { DocumentsData, DocumentItem, DocumentProcessingStatus } from '../types';
import { DUMMY_PREVIOUS_DOCUMENTS } from '../constants';
import {
  fetchOrgDocuments,
  uploadFileDocument,
  uploadTextDocument,
  uploadUrlDocument,
  getProcessingStatus
} from '../api';

interface DocumentsStepProps {
  data: DocumentsData;
  onChange: (patch: Partial<DocumentsData>) => void;
  /** The active groupId needed for API calls. */
  groupId: string;
}

const SourceCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ icon, title, description, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      p: '20px',
      borderRadius: '6px',
      outline: '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))',
      outlineOffset: '-1px',
      cursor: 'pointer',
      display: 'flex',
      gap: '10px',
      transition: 'background 0.15s',
      '&:hover': { background: 'rgba(255,255,255,0.04)' }
    }}
  >
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            fontFamily: 'Inter',
            color: 'var(--Text-text-primary, #FDFDFD)',
            lineHeight: '16px'
          }}
        >
          {title}
        </Typography>
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          fontFamily: 'Inter',
          color: 'var(--Text-text-secondary, #B6B6B6)',
          lineHeight: '20px'
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const DocumentsStep: React.FC<DocumentsStepProps> = ({ data, onChange, groupId }) => {
  const [showFileModal, setShowFileModal] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [uploadedExpanded, setUploadedExpanded] = useState(true);
  const [previousExpanded, setPreviousExpanded] = useState(true);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch previously available org documents on mount
  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    setIsLoadingPrevious(true);

    fetchOrgDocuments(groupId)
      .then((docs) => {
        if (cancelled) return;
        // Exclude documents already in the uploaded list (by id)
        const uploadedIds = new Set(data.uploadedDocuments.map((d) => d.id));
        const previousDocs = docs.filter((d) => !uploadedIds.has(d.id));

        // Preserve existing selection state for docs we already have
        const existingSelections = new Map(
          data.previousDocuments.map((d) => [d.id, d.selected])
        );
        const mergedDocs = previousDocs.map((d) => ({
          ...d,
          selected: existingSelections.get(d.id) ?? d.selected
        }));

        onChange({ previousDocuments: mergedDocs });
      })
      .catch((err) => {
        console.error('[AIMDv2] Failed to fetch org documents', err);
        // Fall back to dummy data if API unavailable
        if (!cancelled && data.previousDocuments.length === 0) {
          onChange({
            previousDocuments: DUMMY_PREVIOUS_DOCUMENTS.map((d) => ({
              ...d,
              processingStatus: 'indexed' as const
            } as DocumentItem))
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPrevious(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // Poll processing status for documents still being processed
  const dataRef = useRef(data);
  dataRef.current = data;

  const pollProcessingStatus = useCallback(async () => {
    const allDocs = [
      ...dataRef.current.uploadedDocuments,
      ...dataRef.current.previousDocuments
    ];
    const pending = allDocs.filter((d) => d.processingStatus === 'processing');
    if (pending.length === 0) return;

    const results = await Promise.allSettled(
      pending.map(async (doc) => {
        const status = await getProcessingStatus(doc.id);
        return { id: doc.id, status };
      })
    );

    let uploadedUpdates: Record<string, DocumentProcessingStatus> = {};
    let previousUpdates: Record<string, DocumentProcessingStatus> = {};

    for (const r of results) {
      if (r.status !== 'fulfilled') {
        console.error('[AIMDv2] Document status poll failed:', (r as PromiseRejectedResult).reason);
        continue;
      }
      const { id, status } = r.value;
      if (status === 'processing') continue;
      const inUploaded = dataRef.current.uploadedDocuments.some((d) => d.id === id);
      if (inUploaded) uploadedUpdates[id] = status;
      else previousUpdates[id] = status;
    }

    const hasUploadedChanges = Object.keys(uploadedUpdates).length > 0;
    const hasPreviousChanges = Object.keys(previousUpdates).length > 0;

    if (hasUploadedChanges || hasPreviousChanges) {
      const patch: Partial<DocumentsData> = {};
      if (hasUploadedChanges) {
        patch.uploadedDocuments = dataRef.current.uploadedDocuments.map((d) =>
          uploadedUpdates[d.id] ? { ...d, processingStatus: uploadedUpdates[d.id] } : d
        );
      }
      if (hasPreviousChanges) {
        patch.previousDocuments = dataRef.current.previousDocuments.map((d) =>
          previousUpdates[d.id] ? { ...d, processingStatus: previousUpdates[d.id] } : d
        );
      }
      onChange(patch);
    }
  }, [onChange]);

  useEffect(() => {
    const allDocs = [...data.uploadedDocuments, ...data.previousDocuments];
    const hasPending = allDocs.some((d) => d.processingStatus === 'processing');
    if (!hasPending) return;

    const intervalId = setInterval(pollProcessingStatus, 5000);
    pollProcessingStatus();

    return () => clearInterval(intervalId);
  }, [data.uploadedDocuments, data.previousDocuments, pollProcessingStatus]);

  // ---- Upload handlers (call real API) ----

  const handleAddFile = async (file: File, instructions: string) => {
    if (!groupId) return;
    setIsUploading(true);
    try {
      const newDoc = await uploadFileDocument(file, groupId, instructions || undefined);
      onChange({ uploadedDocuments: [...data.uploadedDocuments, newDoc] });
    } catch (err) {
      console.error('[AIMDv2] File upload failed', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddText = async (title: string, textContent: string) => {
    if (!groupId) return;
    setIsUploading(true);
    try {
      const newDoc = await uploadTextDocument(title, textContent, groupId);
      onChange({ uploadedDocuments: [...data.uploadedDocuments, newDoc] });
    } catch (err) {
      console.error('[AIMDv2] Text document creation failed', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddUrl = async (title: string, url: string, instructions: string) => {
    if (!groupId) return;
    setIsUploading(true);
    try {
      const newDoc = await uploadUrlDocument(title, url, groupId, instructions || undefined);
      onChange({ uploadedDocuments: [...data.uploadedDocuments, newDoc] });
    } catch (err) {
      console.error('[AIMDv2] URL document creation failed', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleToggleUploaded = (id: string) => {
    const updated = data.uploadedDocuments.map((d) =>
      d.id === id ? { ...d, selected: !d.selected } : d
    );
    onChange({ uploadedDocuments: updated });
  };

  const handleTogglePrevious = (id: string) => {
    const updated = data.previousDocuments.map((d) =>
      d.id === id ? { ...d, selected: !d.selected } : d
    );
    onChange({ previousDocuments: updated });
  };

  const handleRemoveUploaded = (id: string) => {
    onChange({ uploadedDocuments: data.uploadedDocuments.filter((d) => d.id !== id) });
  };

  const hasUploaded = data.uploadedDocuments.length > 0;
  const hasPrevious = data.previousDocuments.length > 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        gap: '28px'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Typography
          sx={{
            fontSize: 30,
            fontWeight: 500,
            fontFamily: 'Inter',
            color: 'var(--Text-text-primary, #FDFDFD)'
          }}
        >
          Document Upload
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: 'Inter',
            color: 'var(--Text-text-secondary, #B6B6B6)',
            lineHeight: '20px'
          }}
        >
          Review your program preview before we proceed to build your program structure.
        </Typography>
      </Box>

      {/* Source cards */}
      <Box sx={{ display: 'flex', gap: '14px', position: 'relative' }}>
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '6px',
              zIndex: 2
            }}
          >
            <CircularProgress size={24} sx={{ color: 'var(--Stroke-highlight, #F79420)' }} />
          </Box>
        )}
        <SourceCard
          icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: 'var(--Text-text-primary, #FDFDFD)' }} />}
          title="Add Files"
          description="Drop in relevant documents like Word Docs or PDFs that contain content relevant to your program."
          onClick={() => setShowFileModal(true)}
        />
        <SourceCard
          icon={<TextSnippetOutlinedIcon sx={{ fontSize: 16, color: 'var(--Stroke-icon-highlight, #FFFFFF)' }} />}
          title="Add Text"
          description="Type anything relevant to your program or copy text from other sources and paste here."
          onClick={() => setShowTextModal(true)}
        />
        <SourceCard
          icon={<LinkOutlinedIcon sx={{ fontSize: 16, color: 'var(--Stroke-icon-highlight, #FFFFFF)' }} />}
          title="Add URL"
          description="Share URLs to webpages or online resources that will help us understand your program."
          onClick={() => setShowUrlModal(true)}
        />
      </Box>

      {/* Uploaded documents section */}
      {hasUploaded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box
            onClick={() => setUploadedExpanded((v) => !v)}
            sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
          >
            {uploadedExpanded ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: 'var(--Stroke-icon-dim, #868686)' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: 'var(--Stroke-icon-dim, #868686)' }} />
            )}
            <Typography sx={{ fontSize: 16, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
              Uploaded Files:
            </Typography>
          </Box>
          <Collapse in={uploadedExpanded}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {data.uploadedDocuments.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  document={doc}
                  onToggle={handleToggleUploaded}
                  onRemove={handleRemoveUploaded}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Previously available documents */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Box
          onClick={() => setPreviousExpanded((v) => !v)}
          sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
        >
          {previousExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: 'var(--Text-text-primary, #FDFDFD)' }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: 'var(--Text-text-primary, #FDFDFD)' }} />
          )}
          <Typography sx={{ fontSize: 16, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
            Previously uploaded documents also available for use:
          </Typography>
        </Box>
        <Collapse in={previousExpanded}>
          {isLoadingPrevious ? (
            <Box aria-busy aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: '20px', py: 1 }}>
              {Array.from({ length: 3 }, (_, i) => (
                <Bone key={i} height={44} />
              ))}
            </Box>
          ) : hasPrevious ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', ...fadeInSx }}>
              {data.previousDocuments.map((doc) => (
                <DocumentRow key={doc.id} document={doc} onToggle={handleTogglePrevious} />
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
              No previously uploaded documents found.
            </Typography>
          )}
        </Collapse>
      </Box>

      {/* Modals */}
      <AddFileModal open={showFileModal} onClose={() => setShowFileModal(false)} onSubmit={handleAddFile} />
      <AddTextModal open={showTextModal} onClose={() => setShowTextModal(false)} onSubmit={handleAddText} />
      <AddUrlModal open={showUrlModal} onClose={() => setShowUrlModal(false)} onSubmit={handleAddUrl} />
    </Box>
  );
};

export default DocumentsStep;
